import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import authRecoveryService from '../../../services/authRecoveryService';

const CaptchaManagementPanel = ({ refreshTrigger, onResetComplete }) => {
  const [diagnostics, setDiagnostics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState('supabase_builtin');

  useEffect(() => {
    loadDiagnostics();
  }, [refreshTrigger]);

  const loadDiagnostics = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await authRecoveryService?.getCaptchaDiagnostics();
      setDiagnostics(data);
    } catch (err) {
      setError(err?.message || 'Failed to load CAPTCHA diagnostics');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    if (!confirm(`Are you sure you want to reset CAPTCHA configuration for ${selectedProvider}?`)) {
      return;
    }

    try {
      setProcessing(true);
      setError('');
      await authRecoveryService?.resetCaptchaConfiguration(selectedProvider);
      alert('CAPTCHA configuration reset successfully');
      await loadDiagnostics();
      onResetComplete?.();
    } catch (err) {
      setError(err?.message || 'Failed to reset CAPTCHA configuration');
    } finally {
      setProcessing(false);
    }
  };

  const getHealthStatusColor = (status) => {
    switch (status) {
      case 'healthy':
        return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400';
      case 'degraded':
        return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400';
      case 'failing':
        return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400';
      default:
        return 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-400';
    }
  };

  const providers = [
    { value: 'supabase_builtin', label: 'Supabase Built-in' },
    { value: 'recaptcha_v2', label: 'reCAPTCHA v2' },
    { value: 'recaptcha_v3', label: 'reCAPTCHA v3' },
    { value: 'hcaptcha', label: 'hCaptcha' }
  ];

  if (loading && diagnostics?.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader2" className="w-6 h-6 text-emerald-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          CAPTCHA Configuration Management
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Reset CAPTCHA settings, clear failure counts, and adjust thresholds
        </p>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* CAPTCHA Diagnostics */}
      <div className="space-y-4">
        {diagnostics?.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 dark:bg-slate-700 rounded-lg">
            <Icon name="Shield" className="w-12 h-12 mx-auto mb-2 text-gray-400" />
            <p className="text-sm text-gray-600 dark:text-gray-400">No CAPTCHA diagnostics available</p>
          </div>
        ) : (
          diagnostics?.map((diagnostic) => (
            <div
              key={diagnostic?.id}
              className="bg-white dark:bg-slate-700 rounded-lg border border-gray-200 dark:border-slate-600 p-4"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <Icon name="Shield" size={20} className="text-emerald-600 dark:text-emerald-400" />
                    <h4 className="text-base font-semibold text-gray-900 dark:text-white">
                      {diagnostic?.captchaProvider?.replace('_', ' ')?.toUpperCase()}
                    </h4>
                    <span className={`px-2 py-1 text-xs font-bold rounded ${getHealthStatusColor(diagnostic?.healthStatus)}`}>
                      {diagnostic?.healthStatus?.toUpperCase()}
                    </span>
                    {diagnostic?.isEnabled ? (
                      <span className="px-2 py-1 text-xs font-bold rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                        ENABLED
                      </span>
                    ) : (
                      <span className="px-2 py-1 text-xs font-bold rounded bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-400">
                        DISABLED
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">Failure Count</p>
                      <p className="text-lg font-bold text-gray-900 dark:text-white">
                        {diagnostic?.failureCount || 0}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">Last Checked</p>
                      <p className="text-sm text-gray-900 dark:text-white">
                        {diagnostic?.lastCheckedAt ? new Date(diagnostic?.lastCheckedAt)?.toLocaleString() : 'Never'}
                      </p>
                    </div>
                    {diagnostic?.lastFailureAt && (
                      <div>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Last Failure</p>
                        <p className="text-sm text-gray-900 dark:text-white">
                          {new Date(diagnostic?.lastFailureAt)?.toLocaleString()}
                        </p>
                      </div>
                    )}
                    {diagnostic?.lastSuccessAt && (
                      <div>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Last Success</p>
                        <p className="text-sm text-gray-900 dark:text-white">
                          {new Date(diagnostic?.lastSuccessAt)?.toLocaleString()}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Reset Configuration */}
      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
        <div className="flex items-start gap-2 mb-4">
          <Icon name="AlertTriangle" className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">Reset CAPTCHA Configuration</p>
            <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
              This will reset failure counts and restore default settings for the selected provider.
            </p>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Select Provider
            </label>
            <select
              value={selectedProvider}
              onChange={(e) => setSelectedProvider(e?.target?.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
            >
              {providers?.map(provider => (
                <option key={provider?.value} value={provider?.value}>
                  {provider?.label}
                </option>
              ))}
            </select>
          </div>

          <Button
            onClick={handleReset}
            disabled={processing}
            loading={processing}
            variant="warning"
            fullWidth
          >
            <Icon name="RotateCcw" size={14} className="mr-1" />
            Reset CAPTCHA Configuration
          </Button>
        </div>
      </div>

      {/* Recommendations */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-start gap-2">
          <Icon name="Info" className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-blue-900 dark:text-blue-200">Recommendations</p>
            <ul className="text-xs text-blue-700 dark:text-blue-300 mt-2 space-y-1 list-disc list-inside">
              <li>Disable CAPTCHA in Supabase Dashboard if not needed</li>
              <li>Monitor failure rates to detect bot attacks</li>
              <li>Consider switching providers if failures persist</li>
              <li>Test CAPTCHA after configuration changes</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CaptchaManagementPanel;